import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  EditOutlined,
  HomeOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  TableOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import MainLayout from '../../components/common/MainLayout';
import {
  useCloneSaveMutation,
  useGetCloneJobQuery,
  useGetSaveComparisonQuery,
  useMoveSaveCursorMutation,
  useRefreshSaveCapabilitiesMutation,
  useUpdateSaveMutation,
} from '../../api/savesV2Api';
import { useGetScenarioRewardsQuery } from '../../api/rewardsApi';
import ComparisonView from '../../components/scenarios/ComparisonView';
import StateCorrectionsPanel, { type CorrectionTarget } from '../../components/saves/StateCorrectionsPanel';
import SaveEventsPanel from '../../components/saves/SaveEventsPanel';
import { useRefreshWorld, useSave, useWorld, useWorldEntities } from '../../contexts/WorldContext';
// ScenarioPlaySurface удалён в Phase 4: scenario play живёт в обычных save-scoped
// страницах (Plantings/Plots/etc.) через WorldContext.
import type {
  CursorMoveEstimate,
  SaveWithProjection,
  ScenarioGoalEvaluation,
  ScenarioGoalSpec,
  ScenarioGoalProgress,
  ScenarioScoring,
  SaveWorldEntities,
} from '../../types/saves';

const { Title, Text, Paragraph } = Typography;

const STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'blue' },
  completed: { label: 'Завершён', color: 'green' },
  archived: { label: 'В архиве', color: 'default' },
  requires_migration: { label: 'Нужна миграция', color: 'red' },
};

const formatDate = (iso?: string | null, fallback = '—') =>
  iso ? dayjs(iso).format('DD.MM.YYYY HH:mm') : fallback;

const formatSimDate = (iso?: string | null, fallback = '—') =>
  iso ? dayjs(iso).format('DD.MM.YYYY') : fallback;

const GOAL_OPERATOR_LABELS: Record<string, string> = {
  gte: '>=',
  gt: '>',
  lte: '<=',
  lt: '<',
  eq: '=',
  between: 'в диапазоне',
};

const formatGoalTarget = (target: ScenarioGoalEvaluation['target'], operator?: string) => {
  if (
    operator === 'between'
    && target
    && typeof target === 'object'
    && Number.isFinite(target.min)
    && Number.isFinite(target.max)
  ) {
    return `${target.min}–${target.max}%`;
  }
  return String(target);
};

const scoreTierFor = (scoring?: ScenarioScoring | null, passedCount = 0): string | null => {
  const tiers = Array.isArray(scoring?.tiers) ? scoring.tiers : [];
  const sorted = [...tiers].sort(
    (left, right) => Number(right.min_passed ?? right.minPassed ?? 0) - Number(left.min_passed ?? left.minPassed ?? 0),
  );
  const matched = sorted.find(
    (candidate) => passedCount >= Number(candidate.min_passed ?? candidate.minPassed ?? 0),
  );
  return matched?.tier != null ? String(matched.tier) : null;
};

interface DeepRewindState {
  estimate: CursorMoveEstimate;
  targetDate: string;
}

const formatEstimate = (estimate: CursorMoveEstimate) => (
  <Descriptions size="small" column={1} bordered>
    <Descriptions.Item label="Тиков пересчёта">
      {estimate.ticks_to_recalculate}
    </Descriptions.Item>
    <Descriptions.Item label="Событий после курсора">
      {estimate.events_after_cursor}
    </Descriptions.Item>
    <Descriptions.Item label="Может стать invalidated">
      {estimate.invalidated_events_estimate}
    </Descriptions.Item>
    <Descriptions.Item label="Оценка времени replay">
      {estimate.estimated_replay_cost_ms} мс
    </Descriptions.Item>
  </Descriptions>
);

const ScenarioBriefingCard: React.FC<{
  narrative?: Record<string, unknown> | null;
}> = ({ narrative }) => {
  const intro = typeof narrative?.intro === 'string' ? narrative.intro : null;
  const objective = typeof narrative?.objective === 'string' ? narrative.objective : null;
  const hint = typeof narrative?.hint === 'string' ? narrative.hint : null;

  if (!intro && !objective && !hint) return null;

  return (
    <Card title="Задача сценария" size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {intro ? <Text>{intro}</Text> : null}
        {objective ? <Text strong>{objective}</Text> : null}
        {hint ? <Alert type="info" showIcon message="Подсказка" description={hint} /> : null}
      </Space>
    </Card>
  );
};

const ScenarioGoalsCard: React.FC<{
  progress: ScenarioGoalProgress | null | undefined;
  cursorSimDate?: string | null;
  templateGoals?: ScenarioGoalSpec[] | null;
  templateScoring?: ScenarioScoring | null;
}> = ({
  progress,
  cursorSimDate,
  templateGoals,
  templateScoring,
}) => {
  const progressGoals = progress?.goals ?? [];
  const progressGoalsById = new Map(progressGoals.map((goal) => [goal.id, goal]));
  const templateGoalIds = new Set((templateGoals ?? []).map((goal) => goal.id));
  const displayGoals = (templateGoals?.length
    ? [
        ...templateGoals.map((goal) => {
          const progressGoal = progressGoalsById.get(goal.id);
          return {
            id: goal.id,
            label: progressGoal?.label || goal.label,
            metric: progressGoal?.metric || goal.metric,
            operator: progressGoal?.operator || goal.operator,
            target: progressGoal?.target ?? goal.target,
            value: progressGoal?.value,
            passed: progressGoal?.passed,
          } satisfies ScenarioGoalEvaluation;
        }),
        ...progressGoals.filter((goal) => !templateGoalIds.has(goal.id)),
      ]
    : progressGoals.length > 0
      ? progressGoals
      : []).filter(Boolean);

  if (!progress) {
    return (
      <Card title="Прогресс целей сценария" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Прогресс целей ещё не рассчитан"
            description={displayGoals.length > 0
              ? (cursorSimDate
                ? `Сценарий сейчас открыт на дату ${formatSimDate(cursorSimDate)}. Сами цели уже зашиты в шаблон ниже, но проекция ещё не вернула их текущий прогресс.`
                : 'Сами цели уже зашиты в шаблон сценария, но проекция ещё не вернула их текущий прогресс.')
              : (cursorSimDate
                ? `Сценарий сейчас открыт на дату ${formatSimDate(cursorSimDate)}. После действий или обновления проекции здесь появится оценка целей.`
                : 'После первого пересчёта сценария здесь появится оценка целей и уровня награды.')}
          />
          {displayGoals.length > 0 ? (
            <div>
              {displayGoals.map((goal) => (
                <div key={goal.id} style={{ marginBottom: 4 }}>
                  <Tag color="blue">…</Tag>
                  <Text strong>{goal.label || goal.id}</Text>
                  <Text type="secondary"> — {goal.metric}</Text>
                  <Text type="secondary"> {GOAL_OPERATOR_LABELS[goal.operator || 'gte'] || goal.operator || 'gte'} </Text>
                  <Text>{formatGoalTarget(goal.target, goal.operator)}</Text>
                </div>
              ))}
            </div>
          ) : null}
        </Space>
      </Card>
    );
  }

  const total = displayGoals.length;
  const passed = displayGoals.filter((goal) => goal.passed).length;
  const hasGoals = displayGoals.length > 0;
  const hasMeasuredGoals = Boolean(displayGoals.some((goal) => goal.value !== undefined && goal.value !== null));
  const derivedScoreTier = scoreTierFor(templateScoring, passed);
  const effectiveScoreTier = hasMeasuredGoals || progress.completed
    ? (derivedScoreTier ?? (progress.scoreTier != null ? String(progress.scoreTier) : null))
    : null;
  const percent = total > 0 ? Math.round((passed / total) * 100) : 0;

  if (!hasGoals) {
    return (
      <Card title="Прогресс целей сценария" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="warning"
            showIcon
            message="Цели сценария не определены или ещё не рассчитаны"
            description="Сейчас нельзя надёжно интерпретировать прогресс вида 0 / 0. Нужно либо настроить цели в шаблоне сценария, либо проверить, что проекция уже пересчитана после прохождения." 
          />
          {progress.scoreTier ? (
            <Alert
              type="info"
              showIcon
              message={`Текущий уровень награды: tier ${String(progress.scoreTier)}`}
              description="Уровень награды считается от числа выполненных целей. Пока сами цели не видны, этот tier нельзя считать объяснимым результатом."
            />
          ) : null}
        </Space>
      </Card>
    );
  }

  return (
    <Card title="Прогресс целей сценария" size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space>
          <Statistic title="Пройдено целей" value={passed} suffix={`/ ${total}`} />
          {effectiveScoreTier ? (
            <Statistic
              title="Текущий tier"
              valueRender={() => <Tag color="gold">Tier {effectiveScoreTier}</Tag>}
              value={0}
            />
          ) : null}
          {progress.completed ? <Tag color="green">Все цели достигнуты</Tag> : null}
        </Space>
        <Progress percent={percent} status={progress.completed ? 'success' : 'active'} />
        <Alert
          type="info"
          showIcon
          message={effectiveScoreTier
            ? `Сейчас сценарий тянет на Tier ${effectiveScoreTier}`
            : 'Tier появится после первых рассчитанных метрик'}
          description={hasMeasuredGoals
            ? 'Tier пересчитывается по числу выполненных целей, используя шкалу из шаблона сценария. Ниже показаны целевые метрики и их текущие значения.'
            : 'Цели уже заданы в шаблоне сценария, но для них ещё нет достаточно рассчитанных значений. Поэтому сейчас показывается только список целей без итогового tier.'}
        />
        {displayGoals.length ? (
          <div>
            {displayGoals.map((goal: ScenarioGoalEvaluation) => (
              <div key={goal.id} style={{ marginBottom: 4 }}>
                <Tag color={goal.passed ? 'green' : goal.value !== undefined && goal.value !== null ? 'default' : 'blue'}>
                  {goal.passed ? '✓' : goal.value !== undefined && goal.value !== null ? '·' : '…'}
                </Tag>
                <Text strong>{goal.label || goal.id}</Text>
                <Text type="secondary"> — {goal.metric}</Text>
                <Text type="secondary"> {GOAL_OPERATOR_LABELS[goal.operator || 'gte'] || goal.operator || 'gte'} </Text>
                <Text>{formatGoalTarget(goal.target, goal.operator)}</Text>
                {goal.value !== undefined && goal.value !== null ? (
                  <Text type="secondary"> (текущее: {String(goal.value)})</Text>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </Card>
  );
};

const TIER_COLORS: Record<string, string> = { gold: 'gold', silver: 'default', bronze: 'volcano' };

const ScenarioRewardsCard: React.FC<{ saveId: string }> = ({ saveId }) => {
  const { data: rewards = [], isLoading } = useGetScenarioRewardsQuery({ saveId });
  if (!isLoading && rewards.length === 0) return null;
  return (
    <Card title="Награды" size="small" style={{ marginBottom: 16 }} loading={isLoading}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {rewards.map((r) => (
          <Space key={r.id} wrap size={8}>
            <Tag color={TIER_COLORS[String(r.reward_type)] || 'blue'}>{r.reward_type}</Tag>
            {r.payload?.scoreTier && <Text type="secondary">tier: {r.payload.scoreTier}</Text>}
            {r.payload?.passedCount != null && r.payload?.total != null && (
              <Text type="secondary">целей: {r.payload.passedCount}/{r.payload.total}</Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>{new Date(r.created_at).toLocaleString('ru-RU')}</Text>
          </Space>
        ))}
      </Space>
    </Card>
  );
};

const RuntimeCard: React.FC<{ runtime: unknown }> = ({ runtime }) => (
  <Card title="Runtime snapshot" size="small" style={{ marginBottom: 16 }}>
    <pre
      style={{
        maxHeight: 280,
        overflow: 'auto',
        background: '#fafafa',
        padding: 12,
        borderRadius: 4,
        fontSize: 12,
      }}
    >
      {JSON.stringify(runtime, null, 2)}
    </pre>
  </Card>
);

const SaveWorldAccessCard: React.FC<{
  saveId: string;
  entities: SaveWorldEntities;
}> = ({ saveId, entities }) => {
  const navigate = useNavigate();
  const fields = entities.fields || [];
  const plots = entities.plots || [];
  const beds = entities.beds || [];
  const plantings = entities.plantings || [];
  const firstField = fields[0];
  const firstPlot = plots[0];
  const firstBed = beds[0];
  const planPath = firstField
    ? `/saves/${saveId}/fields/${firstField.id}/plan`
    : firstPlot
      ? `/saves/${saveId}/plots/${firstPlot.id}/plan`
      : null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <AppstoreOutlined /> Игровой мир
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic title="Поля" value={fields.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Участки" value={plots.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Грядки" value={beds.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Посадки" value={plantings.length} />
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<TableOutlined />}
          disabled={!planPath}
          onClick={() => planPath && navigate(planPath)}
        >
          Открыть grid
        </Button>
        <Button icon={<HomeOutlined />} onClick={() => navigate(`/saves/${saveId}/plantings`)}>
          Посадки
        </Button>
        <Button
          icon={<AppstoreOutlined />}
          disabled={!firstBed}
          onClick={() => firstBed && navigate(`/saves/${saveId}/beds/${firstBed.id}`)}
        >
          Грядки
        </Button>
        <Button icon={<ThunderboltOutlined />} onClick={() => navigate(`/saves/${saveId}/simulation`)}>
          Симуляция
        </Button>
      </Space>

      {fields.length || plots.length ? (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {fields.slice(0, 6).map((field) => {
            const fieldPlots = plots.filter((plot) => plot.fieldId === field.id);
            return (
              <div
                key={field.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'center',
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: 8,
                }}
              >
                <Space wrap>
                  <Text strong>{field.name || 'Поле'}</Text>
                  <Badge count={fieldPlots.length} overflowCount={999} />
                  <Text type="secondary">участков</Text>
                </Space>
                <Space wrap>
                  <Button size="small" onClick={() => navigate(`/saves/${saveId}/fields/${field.id}/info`)}>
                    Инфо
                  </Button>
                  <Button size="small" type="link" onClick={() => navigate(`/saves/${saveId}/fields/${field.id}/plan`)}>
                    План
                  </Button>
                </Space>
              </div>
            );
          })}
          {!fields.length
            ? plots.slice(0, 6).map((plot) => (
                <div
                  key={plot.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'center',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 8,
                  }}
                >
                  <Text strong>{plot.name || 'Участок'}</Text>
                  <Space wrap>
                    <Button size="small" onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/info`)}>
                      Инфо
                    </Button>
                    <Button size="small" type="link" onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/plan`)}>
                      План
                    </Button>
                  </Space>
                </div>
              ))
            : null}
        </Space>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="В этом сейве пока нет полей, участков и грядок"
        />
      )}
    </Card>
  );
};

const CapabilitiesCard: React.FC<{
  save: SaveWithProjection;
  onRefresh: () => void;
  refreshing: boolean;
}> = ({ save, onRefresh, refreshing }) => {
  if (save.type !== 'free') return null;
  const snapshot = save.capabilitiesSnapshot;
  return (
    <Card
      size="small"
      title={
        <Space>
          <SafetyCertificateOutlined /> Доступы (capabilities snapshot)
        </Space>
      }
      extra={
        <Tooltip title="Пересчитать capabilities снапшот по текущей подписке. События, которые перестанут быть разрешены, получат CAPABILITY_REVOKED.">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={onRefresh}
          >
            Обновить
          </Button>
        </Tooltip>
      }
      style={{ marginBottom: 16 }}
    >
      {snapshot ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {snapshot.tier ? (
            <div>
              <CrownOutlined /> <Text strong>Tier:</Text> <Tag color="purple">{snapshot.tier}</Tag>
            </div>
          ) : null}
          {Array.isArray(snapshot.features) && snapshot.features.length > 0 ? (
            <div>
              <Text type="secondary">Фичи: </Text>
              <Space wrap size={[4, 4]}>
                {snapshot.features.map((f) => (
                  <Tag key={f}>{f}</Tag>
                ))}
              </Space>
            </div>
          ) : null}
          <pre
            style={{
              maxHeight: 200,
              overflow: 'auto',
              background: '#fafafa',
              padding: 8,
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </Space>
      ) : (
        <Empty description="Capabilities snapshot не задан" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  );
};

export default function SaveDetailPage() {
  const navigate = useNavigate();
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const world = useWorld();
  const worldEntities = useWorldEntities();
  const correctionTargets = useMemo<CorrectionTarget[]>(() => {
    const beds = (worldEntities.beds || []).map((b) => ({
      kind: 'bed' as const, id: b.id, label: b.name || `Грядка ${b.id.slice(0, 8)}`,
    }));
    const plantings = (worldEntities.plantings || [])
      .filter((p) => p.lifecycleStatus !== 'dead' && !p.inStorage)
      .map((p) => {
        const bed = (worldEntities.beds || []).find((b) => b.id === p.bedId);
        return {
          kind: 'planting' as const, id: p.id,
          label: `${p.crop || 'Посадка'}${bed?.name ? ` · ${bed.name}` : ''}`,
        };
      });
    return [...plantings, ...beds];
  }, [worldEntities.beds, worldEntities.plantings]);
  const refreshWorld = useRefreshWorld();
  const [cursorTarget, setCursorTarget] = useState<Dayjs | null>(null);
  const [cloneTarget, setCloneTarget] = useState<Dayjs | null>(null);
  const [activeCloneJobId, setActiveCloneJobId] = useState<string | null>(null);
  const [deepRewind, setDeepRewind] = useState<DeepRewindState | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm] = Form.useForm();

  const isScenario = save?.type === 'scenario';
  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    isError: comparisonError,
  } = useGetSaveComparisonQuery(saveId || '', { skip: !saveId || !isScenario });
  const [moveCursor, { isLoading: moving }] = useMoveSaveCursorMutation();
  const [cloneSave, { isLoading: cloneEnqueueing }] = useCloneSaveMutation();
  // Polling on the active clone job — RTK Query refetch every 1.5s while a
  // job is in flight. Stops automatically once status is terminal (or
  // activeCloneJobId is reset to null after we navigate).
  const cloneJobQuery = useGetCloneJobQuery(
    { saveId: saveId || '', jobId: activeCloneJobId || '' },
    {
      skip: !saveId || !activeCloneJobId,
      pollingInterval: activeCloneJobId ? 1500 : 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const cloneJob = cloneJobQuery.data;
  const cloning = cloneEnqueueing
    || (Boolean(activeCloneJobId)
      && (!cloneJob || cloneJob.status === 'queued' || cloneJob.status === 'running'));
  const [refreshCapabilities, { isLoading: refreshing }] = useRefreshSaveCapabilitiesMutation();
  const [updateSave, { isLoading: savingSettings }] = useUpdateSaveMutation();

  const canEditSettings = world?.farm?.myRole === 'owner'
    || world?.farm?.myRole === 'manager'
    || Boolean(world?.farm?.isOwner);

  const openSettings = () => {
    settingsForm.setFieldsValue({
      name: world?.farm?.name ?? save?.farmName ?? '',
      locationCoords: world?.farm?.locationCoords ?? '',
      description: world?.farm?.description ?? '',
    });
    setSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    if (!saveId) return;
    try {
      const values = await settingsForm.validateFields();
      await updateSave({
        saveId,
        name: typeof values.name === 'string' ? values.name.trim() : undefined,
        locationCoords: values.locationCoords ? String(values.locationCoords).trim() : null,
        description: values.description ? String(values.description).trim() : null,
      }).unwrap();
      message.success('Настройки сохранены');
      setSettingsOpen(false);
      refreshWorld();
    } catch (err: any) {
      if (err?.errorFields) return; // validation error — fields already highlighted
      message.error(err?.data?.message || 'Не удалось сохранить настройки');
    }
  };

  // Drive UI off the polled clone job. Terminal states (succeeded/failed/
  // cancelled) clear the active job and either navigate to the new save
  // or surface the error.
  useEffect(() => {
    if (!cloneJob || !activeCloneJobId) return;
    if (cloneJob.status === 'succeeded' && cloneJob.targetSaveId) {
      message.destroy('clone-job');
      message.success('Клон готов');
      const targetId = cloneJob.targetSaveId;
      setActiveCloneJobId(null);
      navigate(`/saves/${targetId}`);
    } else if (cloneJob.status === 'failed') {
      message.destroy('clone-job');
      message.error(`Не удалось клонировать: ${cloneJob.error || 'неизвестная ошибка'}`);
      setActiveCloneJobId(null);
    } else if (cloneJob.status === 'cancelled') {
      message.destroy('clone-job');
      message.warning('Клонирование отменено');
      setActiveCloneJobId(null);
    }
  }, [cloneJob, activeCloneJobId, navigate]);

  if (!saveId) {
    return (
      <MainLayout breadcrumb={<Breadcrumb items={[{ title: 'Сейв' }]} />}>
        <Alert type="error" showIcon message="saveId не указан" />
      </MainLayout>
    );
  }

  const statusMeta = STATUS_META[save.status] ?? { label: save.status, color: 'default' };
  const cursorSimDate = save.cursor?.simDate || save.cursor?.sim_date || null;
  const goalProgress = world.runtime?.goalsProgress || null;
  const templateGoals = comparisonData?.scenarioTemplate?.goals ?? null;
  const templateScoring = comparisonData?.scenarioTemplate?.scoring ?? null;
  const templateNarrative = comparisonData?.scenarioTemplate?.narrative ?? null;

  const performMove = async (date: string, confirmDeep = false) => {
    try {
      const body = {
        sim_date: date,
        expected_save_revision: save.revision,
        ...(confirmDeep ? { confirm_deep_rewind: true } : {}),
      };
      const result = await moveCursor({ saveId, body }).unwrap();
      if ((result as { status?: string }).status === 'requires_confirmation') {
        const r = result as Extract<typeof result, { status: 'requires_confirmation' }>;
        setDeepRewind({ estimate: r.estimate, targetDate: date });
        return;
      }
      message.success('Курсор перемещён');
      setCursorTarget(null);
      setDeepRewind(null);
      await refreshWorld();
    } catch (err) {
      // 409 deep rewind comes through as error
      const e = err as { data?: { message?: string; estimate?: CursorMoveEstimate; reason?: string } };
      if (e?.data?.estimate && e.data.reason === 'deep_rewind') {
        setDeepRewind({ estimate: e.data.estimate, targetDate: date });
        return;
      }
      message.error(e?.data?.message || 'Не удалось переместить курсор');
    }
  };

  const handleMoveCursor = () => {
    if (!cursorTarget) {
      message.warning('Выберите дату');
      return;
    }
    const iso = cursorTarget.format('YYYY-MM-DD');
    performMove(iso, false);
  };

  const handleConfirmDeepRewind = async () => {
    if (!deepRewind) return;
    await performMove(deepRewind.targetDate, true);
  };

  const enqueueClone = async (iso: string): Promise<boolean> => {
    try {
      const enqueued = await cloneSave({
        saveId,
        body: { sim_date: iso, expected_save_revision: save.revision },
      }).unwrap();
      setActiveCloneJobId(enqueued.cloneJobId);
      message.loading({
        content: `Клон ставится в очередь (job ${enqueued.cloneJobId.slice(0, 8)}…)`,
        key: 'clone-job',
        duration: 0,
      });
      return true;
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || 'Не удалось поставить клон в очередь');
      return false;
    }
  };

  const handleCloneFromDeepRewind = async () => {
    if (!deepRewind) return;
    const ok = await enqueueClone(deepRewind.targetDate);
    if (ok) setDeepRewind(null);
  };

  const handleCloneSave = () => {
    if (!cloneTarget) {
      message.warning('Выберите дату клонирования');
      return;
    }
    const iso = cloneTarget.format('YYYY-MM-DD');
    Modal.confirm({
      title: 'Клонировать сейв?',
      content: (
        <Space direction="vertical">
          <Text>
            Будет создан новый независимый сейв того же типа с состоянием на дату <Text strong>{iso}</Text>.
          </Text>
          <Text type="secondary">События до этой даты будут скопированы в новый сейв.</Text>
        </Space>
      ),
      okText: 'Клонировать',
      cancelText: 'Отмена',
      onOk: async () => {
        const ok = await enqueueClone(iso);
        if (ok) setCloneTarget(null);
      },
    });
  };

  const handleRefreshCapabilities = () => {
    Modal.confirm({
      title: 'Обновить capabilities snapshot?',
      content: (
        <Space direction="vertical">
          <Text>
            Будет пересобран снапшот доступов из вашей текущей подписки. События, которые перестанут
            быть разрешены, получат код <Text code>CAPABILITY_REVOKED</Text>.
          </Text>
          <Text type="secondary">Действие безопасно — оно не удаляет события, только переотмечает их.</Text>
        </Space>
      ),
      okText: 'Обновить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const result = await refreshCapabilities(saveId).unwrap();
          message.success(
            `Capabilities обновлены. Invalidated событий: ${result.invalidated_events}`
          );
        } catch (err) {
          const e = err as { data?: { message?: string } };
          message.error(e?.data?.message || 'Не удалось обновить capabilities');
        }
      },
    });
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Главная</Link> },
            { title: <Link to="/saves">Сейвы</Link> },
            { title: saveId.slice(0, 8) },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          {save.type === 'free' ? 'Свободный сейв' : 'Сценарий'}{' '}
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
        </Title>
        <Space wrap>
          {canEditSettings ? (
            <Button icon={<EditOutlined />} onClick={openSettings}>
              Настройки фермы
            </Button>
          ) : null}
          <Button onClick={() => refreshWorld()}>Обновить</Button>
        </Space>
      </Space>

      <SaveWorldAccessCard saveId={saveId} entities={worldEntities} />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Метаданные сейва" size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="ID">
                <Text code copyable>{save.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Тип">
                <Tag color={save.type === 'free' ? 'green' : 'orange'}>{save.type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Engine version">{save.engineVersion}</Descriptions.Item>
              <Descriptions.Item label="Revision">{String(save.revision)}</Descriptions.Item>
              <Descriptions.Item label="Cache policy">{save.cachePolicy}</Descriptions.Item>
              {save.farmId ? (
                <Descriptions.Item label="Ферма-контейнер">
                  {save.farmName ? <Text>{save.farmName} </Text> : null}
                  <Text code copyable>{save.farmId}</Text>
                </Descriptions.Item>
              ) : null}
              {save.scenarioTemplateId ? (
                <Descriptions.Item label="Scenario template">
                  <Text code copyable>{save.scenarioTemplateId}</Text>
                  {save.scenarioTemplateVersion != null ? (
                    <Text type="secondary"> v{save.scenarioTemplateVersion}</Text>
                  ) : null}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Создан">{formatDate(save.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Обновлён">{formatDate(save.updatedAt)}</Descriptions.Item>
              <Descriptions.Item label="Последний запуск">
                {formatDate(save.lastPlayedAt, 'не запускался')}
              </Descriptions.Item>
              {save.completedAt ? (
                <Descriptions.Item label="Завершён">
                  {formatDate(save.completedAt)}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Card>

          <Card
            size="small"
            title={
              <Space>
                <ClockCircleOutlined /> Курсор симуляции
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Sim date">
                <Text strong>{formatSimDate(cursorSimDate)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Cursor mode">
                {save.cursor?.mode || save.cursor?.cursor_mode || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Timezone">
                {save.cursor?.timezone || 'UTC'}
              </Descriptions.Item>
              <Descriptions.Item label="Auto-advance">
                {save.cursor?.autoAdvanceEnabled ?? save.cursor?.auto_advance_enabled ? 'Да' : 'Нет'}
              </Descriptions.Item>
            </Descriptions>

            <Space wrap>
              <DatePicker
                placeholder="Новая дата"
                value={cursorTarget}
                // Anchor the picker to the SIM cursor, not real "today", so a
                // rewind to "May" stays in the simulation's year instead of the
                // real-calendar year (off-by-a-year cursor desync).
                defaultPickerValue={cursorSimDate ? dayjs(cursorSimDate) : undefined}
                onChange={setCursorTarget}
              />
              <Button
                type="primary"
                onClick={handleMoveCursor}
                loading={moving}
                disabled={save.status !== 'active'}
              >
                Переместить курсор
              </Button>
            </Space>
          </Card>

          <Card
            size="small"
            title={
              <Space>
                <BranchesOutlined /> Создать ветку (branch save)
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Создаёт независимую копию сейва на выбранную дату — события до неё копируются.
              Используется как «что если…» эксперимент или альтернатива deep rewind.
            </Paragraph>
            <Space wrap>
              <DatePicker
                placeholder="Дата клонирования"
                value={cloneTarget}
                onChange={setCloneTarget}
              />
              <Button onClick={handleCloneSave} loading={cloning}>
                Клонировать сейв
              </Button>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {save.type === 'scenario' ? (
            <ScenarioBriefingCard narrative={templateNarrative} />
          ) : null}
          {save.type === 'scenario' ? (
            <ScenarioGoalsCard
              progress={goalProgress}
              cursorSimDate={cursorSimDate}
              templateGoals={templateGoals}
              templateScoring={templateScoring}
            />
          ) : null}
          {save.type === 'scenario' && saveId ? (
            <ScenarioRewardsCard saveId={saveId} />
          ) : null}
          {save.type === 'scenario' ? (
            <ComparisonView
              data={comparisonData}
              isLoading={comparisonLoading}
              isError={comparisonError}
            />
          ) : null}
          <CapabilitiesCard
            save={save}
            onRefresh={handleRefreshCapabilities}
            refreshing={refreshing}
          />
          <StateCorrectionsPanel
            saveId={save.id}
            sessionId={save.simSessionId || null}
            cursorDate={world.cursor?.simDate || null}
            targets={correctionTargets}
          />
          <SaveEventsPanel save={save} />
          <RuntimeCard runtime={world.runtime} />
        </Col>
      </Row>

      <Modal
        open={!!deepRewind}
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            Глубокий откат курсора
          </Space>
        }
        onCancel={() => setDeepRewind(null)}
        footer={[
          <Button key="cancel" onClick={() => setDeepRewind(null)}>
            Отмена
          </Button>,
          <Button
            key="clone"
            icon={<BranchesOutlined />}
            onClick={handleCloneFromDeepRewind}
            loading={cloning}
          >
            Клонировать вместо отката
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleConfirmDeepRewind}
            loading={moving}
          >
            Подтвердить откат
          </Button>,
        ]}
      >
        {deepRewind ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              Вы перематываете курсор на <Text strong>{formatSimDate(deepRewind.targetDate)}</Text>.
              Эта операция считается тяжёлой — пересчёт может занять время и часть событий рискует
              стать invalidated.
            </Paragraph>
            <Paragraph type="secondary">
              Безопаснее создать <Text strong>ветку</Text> от этой даты, чтобы оставить текущую
              историю нетронутой.
            </Paragraph>
            {formatEstimate(deepRewind.estimate)}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="Настройки фермы"
        open={settingsOpen}
        onOk={handleSaveSettings}
        onCancel={() => setSettingsOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingSettings}
        destroyOnClose
      >
        <Form form={settingsForm} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, whitespace: true, message: 'Укажите название' }]}
          >
            <Input placeholder="Моя ферма" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="locationCoords"
            label="Координаты"
            tooltip="Например: 55.7558, 37.6173"
          >
            <Input placeholder="широта, долгота" allowClear />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} placeholder="Произвольное описание" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
}
